import React, { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { ShopProvider } from "./contexts/ShopContext";
import { TrackingProvider } from "./contexts/TrackingContext";
import { I18nProvider } from "./contexts/I18nContext";
import ScrollToTop from "./components/ScrollToTop";
import AppShell from "./components/AppShell";
import HomePage from "./pages/HomePage";
import SearchPage from "./pages/SearchPage";
import { normalizePublicLink } from "./utils/publicNavigation";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import CartPage from "./pages/CartPage";
import ComparePage from "./pages/ComparePage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmPage from "./pages/OrderConfirmPage";
import PaymentResultPage from "./pages/PaymentResultPage";
import ConsultationPage from "./pages/ConsultationPage";
import RegionalRentalPage from "./pages/RegionalRentalPage";
import SitemapPage from "./pages/SitemapPage";
import CustomerERPPage from "./pages/CustomerERPPage";
import CommunityPage from "./pages/CommunityPage";
import AiToolsPage from "./pages/AiToolsPage";
import GeneratedProductPage from "./pages/GeneratedProductPage";
import TrendSeoPage from "./pages/TrendSeoPage";
import DeliveryStatusPage from "./pages/DeliveryStatusPage";
import QuotePreviewPage from "./pages/QuotePreviewPage";
import StorePage from "./pages/StorePage";
import B2BApplyPage from "./pages/B2BApplyPage";
import B2BStorePage from "./pages/B2BStorePage";
import PolicyPage from "./pages/PolicyPage";
import LegalPage from "./pages/LegalPage";
import SmartRedirect from "./components/SmartRedirect";
import { NaverPayInflowTracker } from "./components/NaverPayButton";
import api from "./api";
import { normalizeRegionKey, regionHasDistrict } from "./data/koreaDistricts";

// The ERP and design tools are only needed when an administrator opens /admin.
const AdminPage = lazy(() => import("./pages/AdminPage"));

// Bots get the server-rendered /rental/<지역>/<구>/<제품> local landing page (channel ①);
// humans landing on the same URL are sent to the real product detail page.
function rentalLocationSlug(text) {
  // LOCGEN-v1 convention, shared with the existing generated product URLs.
  const initial = ["g", "kk", "n", "d", "tt", "r", "m", "b", "pp", "s", "ss", "", "j", "jj", "ch", "k", "t", "p", "h"];
  const medial = ["a", "ae", "ya", "yae", "eo", "e", "yeo", "ye", "o", "wa", "wae", "oe", "yo", "u", "wo", "we", "wi", "yu", "eu", "ui", "i"];
  const final = ["", "k", "k", "ks", "n", "nj", "nh", "t", "l", "lk", "lm", "lb", "ls", "lt", "lp", "lh", "m", "p", "ps", "t", "ss", "ng", "j", "ch", "k", "t", "p", "h"];
  return [...text].map(char => {
    const syllable = char.charCodeAt(0) - 0xAC00;
    return syllable >= 0 && syllable <= 11171
      ? initial[Math.floor(syllable / 588)] + medial[Math.floor((syllable % 588) / 28)] + final[syllable % 28]
      : char;
  }).join("").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export function findRegionalRentalProduct(rows, region, district, slug) {
  const normalizedRegion = normalizeRegionKey(region);
  if (!regionHasDistrict(normalizedRegion, district) || !Array.isArray(rows)) return null;
  const suffix = `-${rentalLocationSlug(normalizedRegion)}-${rentalLocationSlug(district)}`;
  const expectedSlug = slug.endsWith(suffix) ? slug : slug + suffix;
  const matches = rows.filter(product => product.slug === expectedSlug
    && product.district === `${normalizedRegion} ${district}`
    && (product.sale_type === "rental" || product.sale_type === "both"));
  return matches.length === 1 ? matches[0] : null;
}

export function RentalProductRedirect() {
  const { slug, region, district } = useParams();
  const [resolved, setResolved] = useState(null);
  const normalizedRegion = normalizeRegionKey(region);
  const key = `${normalizedRegion}|${district}|${slug}`;
  const listing = `/rental/${encodeURIComponent(normalizedRegion)}/${encodeURIComponent(district)}`;
  useEffect(() => {
    let active = true;
    if (!regionHasDistrict(normalizedRegion, district)) {
      setResolved({ key, target: "/rental" });
      return () => { active = false; };
    }
    const params = new URLSearchParams({ region: normalizedRegion, district });
    api.get(`/api/products-by-region?${params}`).then(response => {
      const product = findRegionalRentalProduct(response.data, normalizedRegion, district, slug);
      if (active) setResolved({ key, target: product ? `/p/${encodeURIComponent(product.slug)}` : listing });
    }).catch(() => {
      if (active) setResolved({ key, target: listing });
    });
    return () => { active = false; };
  }, [normalizedRegion, district, slug, key, listing]);
  return resolved?.key === key ? <Navigate to={resolved.target} replace />
    : <p role="status" className="py-16 text-center">지역별 렌탈 상품을 확인하고 있습니다.</p>;
}

function LegacyPublicRedirect() {
  const location = useLocation();
  const target = normalizePublicLink(`${location.pathname}${location.search}${location.hash}`);
  return <Navigate to={target === `${location.pathname}${location.search}${location.hash}` ? "/my" : target} state={location.state} replace />;
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(err, info) { console.error("React ErrorBoundary:", err, info); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, textAlign: "center" }}>
          <h2 style={{ color: "#c00" }}>페이지 로드 오류</h2>
          <pre style={{ textAlign: "left", maxWidth: 600, margin: "20px auto", background: "#f5f5f5", padding: 16, borderRadius: 8, overflow: "auto", fontSize: 12 }}>
            {this.state.error?.toString()}
            {"\n"}
            {this.state.error?.stack}
          </pre>
          <button onClick={() => window.location.reload()} style={{ padding: "8px 24px", background: "#0071e3", color: "white", border: "none", borderRadius: 8, cursor: "pointer" }}>
            새로고침
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ShopProvider>
        <TrackingProvider>
        <I18nProvider>
        <ScrollToTop />
        <NaverPayInflowTracker />
        <ErrorBoundary>
        <AppShell>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/store" element={<StorePage />} />
              <Route path="/store/:category" element={<StorePage />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/products" element={<LegacyPublicRedirect />} />
              <Route path="/erp" element={<LegacyPublicRedirect />} />
              {/* Legacy routes — 구글 색인된 옷 URL 등 → 시/구 추출 시 해당 지역만탈 페이지 */}
              <Route path="/product/:source/:id" element={<SmartRedirect />} />
              <Route path="/product/*" element={<SmartRedirect />} />
              <Route path="/category/*" element={<SmartRedirect />} />
              <Route path="/copier/:id" element={<SmartRedirect />} />
              <Route path="/copier/*" element={<SmartRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/oauth/*" element={<Navigate to="/login" replace />} />
              <Route path="/dashboard" element={<Navigate to="/my" replace />} />
              <Route path="/my" element={<CustomerERPPage />} />
              <Route path="/admin" element={<Suspense fallback={<div role="status" className="p-10 text-center">관리자 화면을 불러오는 중입니다.</div>}><AdminPage /></Suspense>} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/order-confirm" element={<OrderConfirmPage />} />
              <Route path="/payment-result" element={<PaymentResultPage />} />
              <Route path="/wishlist" element={<Navigate to="/cart" replace />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/consultation" element={<ConsultationPage />} />
              <Route path="/inquiry" element={<LegacyPublicRedirect />} />
              <Route path="/rental" element={<RegionalRentalPage />} />
              <Route path="/rental/:region" element={<RegionalRentalPage />} />
              <Route path="/rental/:region/:district" element={<RegionalRentalPage />} />
              <Route path="/rental/:region/:district/:slug" element={<RentalProductRedirect />} />
              <Route path="/sitemap" element={<SitemapPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/community/:slug" element={<CommunityPage />} />
              <Route path="/ai-tools" element={<AiToolsPage />} />
              <Route path="/agent" element={<Navigate to="/" replace />} />
              <Route path="/p/:slug" element={<GeneratedProductPage />} />
              <Route path="/trend" element={<TrendSeoPage />} />
              <Route path="/trend/:slug" element={<TrendSeoPage />} />
              <Route path="/delivery/:routeCode" element={<DeliveryStatusPage />} />
              <Route path="/digital-twin" element={<Navigate to="/admin" replace />} />
              <Route path="/quote/:token" element={<QuotePreviewPage />} />
              <Route path="/b2b" element={<B2BApplyPage />} />
              <Route path="/b2b/store" element={<B2BStorePage />} />
              <Route path="/policy" element={<PolicyPage />} />
              <Route path="/terms" element={<LegalPage kind="terms" />} />
              <Route path="/privacy" element={<LegalPage kind="privacy" />} />
              {/* ── Catch-all 404: smart redirect (시/구 추출 → 지역만탈, 아니면 홈) ── */}
              <Route path="*" element={<SmartRedirect />} />
            </Routes>
        </AppShell>
        </ErrorBoundary>
        </I18nProvider>
        </TrackingProvider>
        </ShopProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
